import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import type { OptiBaySession, OptiBayStage } from '@/types/optibay';

const sb = supabase as any;

export function useOptiBaySessions(ticketId: string | undefined) {
  return useQuery({
    queryKey: ['optibay-sessions', ticketId],
    queryFn: async (): Promise<OptiBaySession[]> => {
      if (!ticketId) return [];
      const { data, error } = await sb
        .from('optibay_sessions')
        .select('*')
        .eq('ticket_id', ticketId)
        .order('created_at', { ascending: false });
      if (error) throw error;
      return (data ?? []) as OptiBaySession[];
    },
    enabled: !!ticketId,
  });
}

interface CreateSessionParams {
  ticketId: string;
  shopId: string;
  technicianId?: string;
  technicianProfileId?: string;
  symptomText?: string;
}

export function useCreateOptiBaySession() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (params: CreateSessionParams) => {
      // Try to pull symptom from workflow complaints
      let symptom = params.symptomText || '';
      if (!symptom) {
        const { data: complaints } = await supabase
          .from('ticket_workflow_complaints')
          .select('text')
          .eq('ticket_id', params.ticketId)
          .order('created_at', { ascending: false })
          .limit(5);
        if (complaints && complaints.length > 0) {
          symptom = complaints.map((c) => c.text).filter(Boolean).join('\n');
        }
      }
      // Fallback: try customer_request_notes from estimates
      if (!symptom) {
        const { data: est } = await (sb)
          .from('estimates')
          .select('customer_request_notes')
          .eq('ticket_id', params.ticketId)
          .limit(1)
          .single();
        if (est?.customer_request_notes) {
          symptom = est.customer_request_notes;
        }
      }
      const { data, error } = await sb
        .from('optibay_sessions')
        .insert({
          ticket_id: params.ticketId,
          shop_id: params.shopId,
          technician_id: params.technicianId || null,
          technician_profile_id: params.technicianProfileId || null,
          symptom_text: symptom || null,
          status: 'active',
          current_stage: 'symptom',
        })
        .select()
        .single();
      if (error) throw error;
      return data as OptiBaySession;
    },
    onSuccess: (_, vars) => {
      qc.invalidateQueries({ queryKey: ['optibay-sessions', vars.ticketId] });
    },
  });
}

export function useUpdateOptiBaySession() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({ sessionId, updates }: {
      sessionId: string;
      updates: Partial<Pick<OptiBaySession, 'system_family_id' | 'subfamily_id' | 'current_stage' | 'status' | 'completed_at' | 'technician_symptom_notes' | 'technician_symptom_tags'>>;
    }) => {
      const { data, error } = await sb
        .from('optibay_sessions')
        .update(updates)
        .eq('id', sessionId)
        .select()
        .single();
      if (error) throw error;
      return data as OptiBaySession;
    },
    onSuccess: (data: any) => {
      qc.invalidateQueries({ queryKey: ['optibay-sessions', data.ticket_id] });
    },
  });
}
